/**
 * Hook for loading session data from the server
 */
import { useEffect, useRef } from "react";
import type { UpdateEvent } from "../../../types/domain";
import { parseTranscriptToEvents } from "../lib/transcriptParser";

type UseSessionDataOptions = {
  API: string;
  effectiveSid: string | undefined;
  authedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  setEvents: React.Dispatch<React.SetStateAction<UpdateEvent[]>>;
  setLatestSeq: (seq: number) => void;
  setSummary: (s: string) => void;
  setSessionDate: (d: string) => void;
  setPinnedSeqs: React.Dispatch<React.SetStateAction<Set<number>>>;
  setSimilarSessionIds: (ids: string[]) => void;
  setAssessmentIds: (ids: string[]) => void;
  setExtraNotes: (notes: string) => void;
  /** Session's stored language ("en"/"hu"); used to prefill the clinical-note generator. */
  setLang?: (lang: "en" | "hu" | undefined) => void;
  latestSeqRef: React.MutableRefObject<number>;
  summaryRef: React.MutableRefObject<{ setDraft?: (s: string) => void } | null>;
};

export function useSessionData({
  API,
  effectiveSid,
  authedFetch,
  setEvents,
  setLatestSeq,
  setSummary,
  setSessionDate,
  setPinnedSeqs,
  setSimilarSessionIds,
  setAssessmentIds,
  setExtraNotes,
  setLang,
  latestSeqRef,
  summaryRef,
}: UseSessionDataOptions) {
  const fetchRef = useRef(authedFetch);
  useEffect(() => {
    fetchRef.current = authedFetch;
  }, [authedFetch]);

  const lastLoadedSidRef = useRef<string | undefined>(undefined);
  // Counter that increments on every session switch so we can detect when
  // rapid A->B->A navigation means we need to reload session A even though
  // lastLoadedSidRef already holds "A" (the intermediate B switch cleared state).
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    if (!effectiveSid || !API) return;

    // Increment generation on every run so the async callback can detect staleness.
    loadGenerationRef.current += 1;
    const thisGeneration = loadGenerationRef.current;

    // Even if we previously loaded this session, the state may have been
    // cleared by an intermediate session switch (A->B->A), so we must
    // always re-fetch when effectiveSid changes. We track the last loaded
    // ID only to skip the initial clear when it hasn't changed.
    const needsClear = lastLoadedSidRef.current !== effectiveSid;
    lastLoadedSidRef.current = effectiveSid;

    if (needsClear) {
      setSummary("");
      summaryRef.current?.setDraft?.("");
      setEvents([]);
      setLatestSeq(0);
      setPinnedSeqs(new Set());
      setSimilarSessionIds([]);
      setAssessmentIds([]);
      setExtraNotes("");
      setLang?.(undefined);
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchRef.current(`${API}/sessions/${encodeURIComponent(effectiveSid)}`, {
          method: "GET",
        });
        const raw = await resp.text();
        if (!resp.ok) throw new Error(raw || resp.statusText);
        const data = raw ? JSON.parse(raw) : {};
        const item = data?.item || data || {};
        if (cancelled || thisGeneration !== loadGenerationRef.current) return;

        if (item.summary != null) {
          setSummary(String(item.summary));
          summaryRef.current?.setDraft?.(String(item.summary || ""));
        }

        const loadedDate =
          (typeof item.session_date === "string" && item.session_date) ? item.session_date :
          (typeof item.sessionDate === "string" && item.sessionDate) ? item.sessionDate :
          (typeof item.date === "string" && item.date) ? item.date :
          "";

        if (loadedDate) setSessionDate(loadedDate);

        const loadedLang: "en" | "hu" | undefined =
          item.lang === "en" || item.lang === "hu" ? item.lang : undefined;
        setLang?.(loadedLang);

        // Prefer raw_transcript (NDJSON with timing) over transcript (plain text)
        // raw_transcript has chunk timing data needed for replay
        const hasRawTranscript = typeof item.raw_transcript === "string" && item.raw_transcript.trim();
        const hasTranscript = typeof item.transcript === "string" && item.transcript.trim();

        // NOTE: never log transcript contents (PHI) — lengths/booleans only.
        console.log("[useSessionData] Transcript data:", {
          hasRawTranscript,
          rawTranscriptLength: item.raw_transcript?.length || 0,
          hasTranscript,
          transcriptLength: item.transcript?.length || 0,
        });

        const transcriptText: string | undefined =
          hasRawTranscript
            ? item.raw_transcript
            : hasTranscript
            ? item.transcript
            : undefined;

        if (transcriptText && transcriptText.trim()) {
          const evs = parseTranscriptToEvents(transcriptText);
          const maxEndSec = evs.reduce((m, e: any) => typeof e?.end_sec === "number" && e.end_sec > m ? e.end_sec : m, 0);
          console.log("[DBG useSessionData] SET sessionId=", effectiveSid, "count=", evs.length, "maxEndSec=", maxEndSec, "source=", hasRawTranscript ? "raw_transcript" : "transcript");
          setEvents(evs as any);
          if (evs.length) {
            const maxSeq = Math.max(...evs.map((x: any) => Number(x.seq || 0)));
            setLatestSeq(maxSeq);
            latestSeqRef.current = maxSeq;
          }
        }
      } catch (e: any) {
        console.warn("[useSessionData] load session failed", e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    effectiveSid,
    setEvents,
    setLatestSeq,
    setSummary,
    setSessionDate,
    setPinnedSeqs,
    setSimilarSessionIds,
    setAssessmentIds,
    setExtraNotes,
    setLang,
    API,
    latestSeqRef,
    summaryRef,
  ]);

  return { lastLoadedSidRef };
}
